import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, X, Crown, ShieldCheck } from "@phosphor-icons/react";

const EMPTY = { name: "", permissions: [] };

export default function RolesAdmin() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([api.get("/roles"), api.get("/permissions/catalog")]);
    setRoles(r.data);
    setCatalog(c.data.permissions);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome do perfil");
    try {
      if (editing) {
        await api.put(`/roles/${editing}`, form);
        toast.success("Perfil atualizado");
      } else {
        await api.post("/roles", form);
        toast.success("Perfil criado");
      }
      setForm(EMPTY); setEditing(null); setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const startEdit = (r) => {
    setEditing(r.id);
    setForm({ name: r.name, permissions: [...(r.permissions || [])] });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (r) => {
    if (!window.confirm(`Excluir o perfil "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Perfil removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    }
  };

  const groupedCatalog = catalog.reduce((acc, p) => {
    const group = p.key.split(".")[0];
    (acc[group] ||= []).push(p);
    return acc;
  }, {});

  return (
    <div data-testid="roles-admin-page" className="space-y-8">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="label-brutal">Administração</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black">Perfis de Acesso</h1>
          <p className="text-brand-ink2 mt-2">Defina o que cada perfil pode fazer no sistema.</p>
        </div>
        {!showForm && (
          <button data-testid="new-role-btn" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(true); }} className="btn-pink">
            <Plus weight="bold" size={18} /> Novo perfil
          </button>
        )}
      </header>

      {showForm && (
        <form onSubmit={submit} className="card-brutal p-6 space-y-5">
          <div>
            <label className="label-brutal">Nome do perfil *</label>
            <input data-testid="role-name-input" required className="input-brutal max-w-md" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Vendedor Sênior" />
          </div>

          <div>
            <p className="label-brutal">Permissões</p>
            <div className="space-y-4">
              {Object.entries(groupedCatalog).map(([group, items]) => (
                <div key={group} className="border-2 border-brand-ink bg-brand-yellow p-4">
                  <p className="font-heading font-black uppercase text-xs tracking-widest mb-3">{groupLabel(group)}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {items.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 bg-white border-2 border-brand-ink px-3 py-2 cursor-pointer hover:bg-brand-yellow">
                        <input
                          type="checkbox"
                          data-testid={`perm-${p.key}`}
                          checked={form.permissions.includes(p.key)}
                          onChange={() => toggle(p.key)}
                          className="w-4 h-4 accent-black"
                        />
                        <span className="text-sm font-bold flex-1">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); setShowForm(false); }} className="btn-white">
              <X weight="bold" size={16} /> Cancelar
            </button>
            <button data-testid="save-role-btn" type="submit" className="btn-pink">
              {editing ? <><FloppyDisk weight="bold" size={16} />Salvar</> : <><Plus weight="bold" size={16} />Criar perfil</>}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.id} data-testid={`role-card-${r.id}`} className={`card-brutal p-5 ${r.is_master_role ? "bg-brand-pink text-white" : ""}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {r.is_master_role ? <Crown weight="bold" size={18} /> : <ShieldCheck weight="bold" size={18} />}
                <h3 className="font-heading text-xl font-black">{r.name}</h3>
              </div>
              {!r.is_master_role && (
                <div className="flex gap-1">
                  <button data-testid={`edit-role-${r.id}`} onClick={() => startEdit(r)} className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-yellow">
                    <PencilSimple weight="bold" size={12} />
                  </button>
                  <button data-testid={`delete-role-${r.id}`} onClick={() => remove(r)} className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                    <Trash weight="bold" size={12} />
                  </button>
                </div>
              )}
            </div>
            <p className={`text-sm ${r.is_master_role ? "text-white/90" : "text-brand-ink2"}`}>
              {r.is_master_role ? "Todas as permissões (não editável)" : `${r.permissions?.length || 0} permissão(ões)`}
            </p>
            <p className={`text-xs mt-3 ${r.is_master_role ? "text-white/80" : "text-brand-ink2"}`}>
              {r.members_count || 0} usuário(s) com este perfil
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function groupLabel(g) {
  const map = {
    dashboard: "Dashboard",
    lives: "Lives & Pedidos",
    products: "Produtos",
    departments: "Departamentos",
    customers: "Clientes",
    contractors: "Contratantes",
    loyalty: "Fidelidade",
    reconcile: "Conciliação",
    settings: "Configurações",
    admin: "Administração",
  };
  return map[g] || g;
}
